import { api } from '../../../lib/api';
import type { TassaLite } from '../fisco/fiscoApi';

/**
 * Debiti col fisco (`/admin/debiti`): la bacheca. I conti li fa l'API
 * (admin.debiti.ts); qui solo i tipi e la query.
 */

export type ColonnaDebiti = 'sistemare' | 'rate' | 'chiusi';
export type DocumentoDebito = 'CARTELLA' | 'AVVISO' | 'PIANO' | 'F24';

export interface RataDebito {
  id: string;
  seq: number;
  scadenza: string;
  importo: number;
  pagataIl: string | null;
  nota: string | null;
}

export interface CartaDebito {
  id: string;
  colonna: ColonnaDebiti;
  documento: DocumentoDebito | null;
  titolo: string;
  numero: string | null;
  notificataIl: string | null;
  importo: number;
  scadenza: string | null;
  giorni: number | null;
  stato: string;
  urgente: boolean;
  cosaFare: string | null;
  nota: string | null;
  rate: {
    totali: number;
    pagate: number;
    pagato: number;
    resta: number;
    prossima: RataDebito | null;
    elenco: RataDebito[];
  } | null;
  dentro: Array<{ cosa: string; importo: number }>;
  ultimoPagamento: string | null;
  tassa: TassaLite;
  rataDaPagare: { id: string; importo: number; seq: number } | null;
}

export interface ColonnaBacheca {
  key: ColonnaDebiti;
  label: string;
  descrizione: string;
  totale: number;
  totaleLabel: string;
  carte: CartaDebito[];
}

export interface BachecaDebiti {
  oggi: string;
  colonne: ColonnaBacheca[];
}

export const debitiBachecaQuery = {
  queryKey: ['admin', 'debiti', 'bacheca'] as const,
  queryFn: () => api.get<BachecaDebiti>('/admin/debiti/bacheca'),
  staleTime: 30_000,
};

// ── Calendario dei pagamenti ─────────────────────────────────────────

export type TipoVoce = 'fisco' | 'stipendi' | 'spesa' | 'fornitore' | 'incasso';
export type StatoVoce = 'pagato' | 'scaduto' | 'da_pagare';

export interface VoceCalendario {
  key: string;
  tipo: TipoVoce;
  titolo: string;
  importo: number;
  verso: 'esce' | 'entra';
  stato: StatoVoce;
  come: string | null;
  daDecidere: boolean;
  stima: boolean;
  pagataIl: string | null;
  debitoId: string | null;
  tassa?: TassaLite | null;
  installmentId?: string | null;
  /** Cosa controllare prima di quel giorno (es. il conto in positivo per un addebito di Qonto). */
  avviso?: string | null;
}

export interface GiornoCalendario {
  data: string;
  voci: VoceCalendario[];
  saldo: number | null;
}

export interface MeseCalendario {
  mese: string;
  nome: string;
  esceCerto: number;
  esceDaDecidere: number;
  entra: number;
  saldoFine: number | null;
}

export interface CalendarioPagamenti {
  oggi: string;
  da: string;
  a: string;
  saldoOggi: number;
  giorni: GiornoCalendario[];
  mesi: MeseCalendario[];
  /** Il primo giorno sotto zero: da lì si comincia a usare il fido Qonto. */
  primoBuco: { data: string; saldo: number } | null;
  /** Il fido Qonto (26/10/2026): il conto può scendere fino a -fido. */
  fido: number;
  /** Il primo giorno in cui il saldo previsto supera anche il fido. */
  oltreFido: { data: string; saldo: number } | null;
}

/**
 * Il tono di un saldo previsto: sopra zero niente; fra zero e -fido si usa il
 * fido (neutro, il conto regge); oltre il fido è rosso. Solo i token: il
 * progetto è monocromo, il rosso è l'unico colore di allarme.
 */
export function tonoSaldo(saldo: number | null | undefined, fido: number): '' | 'is-fido' | 'is-neg' {
  if (saldo == null || saldo >= 0) return '';
  return saldo < -fido ? 'is-neg' : 'is-fido';
}

/** "oggi", "domani" o "26 ott": per le frasi brevi del calendario. */
export function quandoBreve(iso: string, oggi: string): string {
  if (iso === oggi) return 'oggi';
  const d = new Date(`${oggi}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  if (iso === d.toISOString().slice(0, 10)) return 'domani';
  return dataCorta(iso, oggi);
}

/** Dal primo del mese di oggi alla fine del mese `mesi - 1` dopo. */
export function calendarioQuery(oggi: Date, mesi: number) {
  const y = oggi.getFullYear();
  const m = oggi.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const da = `${y}-${pad(m + 1)}-01`;
  const fine = new Date(Date.UTC(y, m + mesi, 0));
  const a = `${fine.getUTCFullYear()}-${pad(fine.getUTCMonth() + 1)}-${pad(fine.getUTCDate())}`;
  return {
    queryKey: ['admin', 'debiti', 'calendario', da, a] as const,
    queryFn: () => api.get<CalendarioPagamenti>(`/admin/debiti/calendario?da=${da}&a=${a}`),
    staleTime: 30_000,
    placeholderData: (prev: CalendarioPagamenti | undefined) => prev,
  };
}

/** L'etichetta piccola sopra il titolo della carta: che documento è. */
export const DOCUMENTO_LABEL: Record<DocumentoDebito, string> = {
  CARTELLA: 'Cartella',
  AVVISO: 'Avviso',
  PIANO: 'Piano a rate',
  F24: 'F24 non pagato',
};

/**
 * "il 5 nov", ma "l'11 nov", "l'1", "l'8" (26/10/2026: "entro il 11 nov" in 72 righe).
 * `prima` è la parola davanti: 'il' (entro il, scaduta il, pagata il) o 'dal'/'al'/'del'.
 */
export function conArticolo(iso: string | null | undefined, oggi?: string, prima: 'il' | 'al' | 'dal' | 'del' = 'il'): string {
  const d = dataCorta(iso, oggi);
  if (!d) return '';
  const g = Number(d.split(' ')[0]);
  const elide = g === 1 || g === 8 || g === 11;
  const forme = { il: elide ? "l'" : 'il ', al: elide ? "all'" : 'al ', dal: elide ? "dall'" : 'dal ', del: elide ? "dell'" : 'del ' };
  return `${forme[prima]}${d}`;
}

/** "11/11/2026" → "11 nov 2026"; senza anno se è quello di oggi. */
export function dataCorta(iso: string | null | undefined, oggi?: string): string {
  if (!iso) return '';
  const [y, m, g] = iso.slice(0, 10).split('-').map(Number);
  const mesi = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
  const stessoAnno = oggi ? oggi.slice(0, 4) === String(y) : false;
  return `${g} ${mesi[(m ?? 1) - 1]}${stessoAnno ? '' : ` ${y}`}`;
}
